import { createUseContext, sleep } from '@frontend/helpers'
import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { MeQuery, useMeQuery } from 'src/generated/apollo'
import { tenantStorage } from 'src/pages'
import { useOAuth2 } from 'src/utils/oauth2'
import { authTokenStorage } from './apollo'
import { languageStorage } from 'src/App'

interface MeContextShape {
  authToken: string
  login(authToken: string): void
  refetch: () => void
  me?: MeQuery['me']
  loadingLanguage?: boolean
}

export const {
  context: MeContext,
  use: useMe,
  Provider,
} = createUseContext<MeContextShape>()

export function MeProvider(props: PropsWithChildren<{}>) {
  const { children } = props
  const [loadingLanguage, setLl] = useState(true)
  const navigate = useNavigate()
  const { isOAuth2LoginScene } = useOAuth2()
  const [authToken, setAuthToken] = useState<string>(
    authTokenStorage.get() || '',
  )
  const { data, refetch } = useMeQuery({
    skip: !authToken,
    onCompleted: () => {
      setLl(false)
    },
  })
  useEffect(() => {
    if (
      data?.me.tenant.membersCannotModifyLanguage &&
      data.me.tenant.defaultLanguage
    )
      languageStorage.set(data.me.tenant.defaultLanguage)
    else if (data?.me.language) languageStorage.set(data?.me.language)
  }, [data?.me])
  const login = useCallback<MeContextShape['login']>(
    async (authToken) => {
      const isRegistry = window.location.pathname.startsWith('/register')
      if (!isRegistry) {
        tenantStorage.set(
          window.location.pathname.replace(new RegExp('/login/*'), ''),
        )
      }
      authTokenStorage.set(authToken)
      setAuthToken(authToken)
      setLl(true)
      try {
        const { data } = await refetch()
        if (
          data?.me.tenant.membersCannotModifyLanguage &&
          data.me.tenant.defaultLanguage
        )
          languageStorage.set(data.me.tenant.defaultLanguage)
        else if (data?.me.language) languageStorage.set(data?.me.language)
        setLl(false)
      } catch (error) {
        setLl(false)
      }

      if (!isOAuth2LoginScene) {
        navigate('/dashboard')
      }
    },
    [data?.me.language, isOAuth2LoginScene, navigate],
  )

  return (
    <Provider
      value={{
        authToken,
        login,
        me: data?.me,
        refetch,
        loadingLanguage: loadingLanguage,
      }}
    >
      {children}
    </Provider>
  )
}
